/**
 * Route protection: gates the whole app behind admin status (existence of the
 * caller's own `admins/{uid}` doc — see lib/auth.tsx).
 *  - loading  → full-screen spinner
 *  - signed-out → <LoginScreen>
 *  - not-authorized → <NotAuthorizedScreen> (hard reject; auth.tsx already
 *    signed the account out)
 *  - admin → render children
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../lib/auth';
import { sendAdminCode, verifyAdminCode } from '../lib/clerkLogin';
import { errorMessage } from '../lib/errors';
import { Spinner } from './States';
import { Button } from './ui';

export function RequireAdmin({ children }: { children: ReactNode }): JSX.Element {
  const { status } = useAuth();

  if (status === 'loading') {
    return (
      <div className="grid h-full place-items-center bg-slate-50">
        <Spinner label="Verifying access…" />
      </div>
    );
  }
  if (status === 'signed-out') return <LoginScreen />;
  if (status === 'not-authorized') return <NotAuthorizedScreen />;
  return <>{children}</>;
}

/**
 * Admin login — EMAIL OTP (no passwords), same flow as the dealer app.
 * Step 1: email → Clerk emails a 6-digit code. Step 2: code → Firebase session.
 * Admin rights still require an `admins/{uid}` doc (see AuthProvider).
 */
function LoginScreen(): JSX.Element {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const send = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await sendAdminCode(email);
      setStep('code');
      setCode('');
      setCooldown(60);
    } catch (e2) {
      setErr(errorMessage(e2));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await verifyAdminCode(email, code.trim());
      // onAuthStateChanged flips status to admin / not-authorized.
    } catch (e2) {
      setErr(errorMessage(e2));
      setBusy(false);
    }
  };

  return (
    <div className="grid h-full place-items-center bg-slate-50 p-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-brand-600 text-sm font-bold text-white">
            A
          </span>
          <div>
            <p className="text-base font-semibold text-slate-900">ANYWAY IT SOLUTIONS — Admin</p>
            <p className="text-xs text-slate-500">Authorized personnel only</p>
          </div>
        </div>

        {step === 'email' ? (
          <form onSubmit={send} className="space-y-4">
            <div>
              <label htmlFor="email" className="mb-1 block text-xs font-medium text-slate-600">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                placeholder="you@company.com"
              />
              <p className="mt-1 text-[11px] text-slate-400">
                We&apos;ll email you a one-time code — no password needed.
              </p>
            </div>
            {err && (
              <p className="rounded-md bg-red-50 px-3 py-2 text-xs font-medium text-red-700">{err}</p>
            )}
            <Button type="submit" loading={busy} className="w-full">
              Email me a code
            </Button>
          </form>
        ) : (
          <form onSubmit={verify} className="space-y-4">
            <div>
              <label htmlFor="code" className="mb-1 block text-xs font-medium text-slate-600">
                One-time code
              </label>
              <input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                value={code}
                onChange={(ev) => setCode(ev.target.value.replace(/\D/g, ''))}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm tracking-[0.4em] outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                placeholder="••••••"
              />
              <p className="mt-1 text-[11px] text-slate-400">Sent to {email}. Check spam too.</p>
            </div>
            {err && (
              <p className="rounded-md bg-red-50 px-3 py-2 text-xs font-medium text-red-700">{err}</p>
            )}
            <Button type="submit" loading={busy} className="w-full">
              Verify &amp; sign in
            </Button>
            <div className="flex items-center justify-between text-xs">
              <button
                type="button"
                className="font-medium text-slate-500 hover:underline disabled:opacity-40"
                disabled={cooldown > 0 || busy}
                onClick={() => void send()}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
              </button>
              <button
                type="button"
                className="font-medium text-slate-500 hover:underline"
                onClick={() => {
                  setStep('email');
                  setErr(null);
                }}
              >
                Change email
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function NotAuthorizedScreen(): JSX.Element {
  const { email, signOut } = useAuth();
  return (
    <div className="grid h-full place-items-center bg-slate-50 p-4">
      <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-red-100 text-2xl text-red-600">
          ⚠
        </div>
        <h1 className="text-lg font-semibold text-slate-900">Not authorized</h1>
        <p className="mt-2 text-sm text-slate-600">
          {email ? <span className="font-medium">{email}</span> : 'This account'} is not an admin.
          There is no <code className="rounded bg-slate-100 px-1">admins/&#123;uid&#125;</code>{' '}
          record for it, so access to this console is denied. The account has been signed out.
        </p>
        <p className="mt-2 text-xs text-slate-500">
          If you believe this is a mistake, ask a super-admin to create your
          <code className="mx-1 rounded bg-slate-100 px-1">admins/&#123;uid&#125;</code>doc in the
          Firebase console, then sign in again.
        </p>
        <div className="mt-6">
          <Button variant="danger" onClick={() => void signOut()} className="w-full">
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
